import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Alert } from 'react-bootstrap';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [recentIncome, setRecentIncome] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all dashboard data in parallel
      const [currenciesResponse, incomeResponse, expensesResponse] = await Promise.all([
        apiService.currencies.getAll(),
        apiService.income.getAll(),
        apiService.expenses.getAll()
      ]);

      // Set currencies
      if (currenciesResponse.data.status === 'success') {
        setCurrencies(currenciesResponse.data.data);
      }

      let allIncome = [];
      let allExpenses = [];

      // Set recent transactions (last 5) and get all data for stats calculation
      if (incomeResponse.data.status === 'success') {
        allIncome = incomeResponse.data.data;
        setRecentIncome(allIncome.slice(0, 5));
      }

      if (expensesResponse.data.status === 'success') {
        allExpenses = expensesResponse.data.data;
        setRecentExpenses(allExpenses.slice(0, 5));
      }

      // Calculate stats from actual user data
      const totalIncome = allIncome.reduce((sum, item) => sum + parseFloat(item.amount_in_pln || 0), 0);
      const totalExpenses = allExpenses.reduce((sum, item) => sum + parseFloat(item.amount_in_pln || 0), 0);
      const balance = totalIncome - totalExpenses;

      setStats({
        total_income: totalIncome,
        total_outcome: totalExpenses,
        balance: balance
      });

    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'PLN') => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h1 className="text-white mb-0">
              <i className="fas fa-chart-line me-3"></i>
              Dashboard
            </h1>
            <p className="text-light opacity-75">Overview of your Vienna trip budget</p>
          </Col>
        </Row>

        {error && (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        {/* Budget Statistics */}
        <Row className="mb-4">
          <Col md={4}>
            <div className="stats-card">
              <h3 className="mb-2">
                <i className="fas fa-plus-circle me-2 text-success"></i>
                Total Income
              </h3>
              <h2 className="mb-0 fw-bold">
                {stats ? formatAmount(stats.total_income) : '0.00 PLN'}
              </h2>
              <small className="opacity-75">All income in PLN</small>
            </div>
          </Col>
          
          <Col md={4}>
            <div className="stats-card">
              <h3 className="mb-2">
                <i className="fas fa-minus-circle me-2 text-danger"></i>
                Total Expenses
              </h3>
              <h2 className="mb-0 fw-bold">
                {stats ? formatAmount(stats.total_outcome) : '0.00 PLN'}
              </h2>
              <small className="opacity-75">All expenses in PLN</small>
            </div>
          </Col>
          
          <Col md={4}>
            <div className="stats-card">
              <h3 className="mb-2">
                <i className="fas fa-wallet me-2 text-warning"></i>
                Balance
              </h3>
              <h2 className={`mb-0 fw-bold ${stats && stats.balance >= 0 ? 'text-success' : 'text-danger'}`}>
                {stats ? formatAmount(stats.balance) : '0.00 PLN'}
              </h2>
              <small className="opacity-75">Remaining budget</small>
            </div>
          </Col>
        </Row>

        {/* Currencies */}
        <Row className="mb-4">
          <Col>
            <Card className="vienna-card">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-coins me-2"></i>
                  Exchange Rates
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  {currencies.map((currency) => (
                    <Col sm={6} md={4} key={currency.id} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="currency-badge">{currency.code}</span>
                        <span className="fw-bold">
                          {currency.is_master_currency ? 
                            'Master Currency' : 
                            `${currency.exchange_rate} PLN`
                          }
                        </span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Transactions */}
        <Row>
          {/* Recent Income */}
          <Col md={6} className="mb-4">
            <Card className="vienna-card">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-plus-circle me-2 text-success"></i>
                  Recent Income
                </h5>
              </Card.Header>
              <Card.Body>
                {recentIncome.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentIncome.map((income) => (
                        <tr key={income.id}>
                          <td>{income.description || 'No description'}</td>
                          <td className="amount-positive">
                            {formatAmount(income.amount, income.currency_code)}
                          </td>
                          <td>{formatDate(income.create_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted text-center py-4">No income entries yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Recent Expenses */}
          <Col md={6} className="mb-4">
            <Card className="vienna-card">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-minus-circle me-2 text-danger"></i>
                  Recent Expenses
                </h5>
              </Card.Header>
              <Card.Body>
                {recentExpenses.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentExpenses.map((expense) => (
                        <tr key={expense.id}>
                          <td>{expense.description || 'No description'}</td>
                          <td className="amount-negative">
                            -{formatAmount(expense.amount, expense.currency_code)}
                          </td>
                          <td>{formatDate(expense.create_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted text-center py-4">No expenses yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DashboardPage; 